/*
 * Dos dominios, un solo portal y una sola sesión.
 *
 *   mail.orvian.mx   → correo, Drive, ajustes, admin, el inicio de sesión y la app del teléfono (/app/office)
 *                      (hasta 2026-10-02 era email.orvian.mx, que sigue vivo como alias: ver CORREO_ALIAS)
 *   docs.orvian.mx   → documentos, hojas de cálculo, presentaciones (y lo que abre Orvian Office)
 *                      y los enlaces que se comparten (/open/)
 *   meet.orvian.mx   → Orvian Meet (videollamadas): `/` = inicio y `/abc-defg-hij` = una reunión
 *
 * Es el MISMO build servido por los dos vhosts. Este módulo decide en qué dominio va cada ruta;
 * la sesión se la pasa el correo a docs en `sesionCorreo.js`. Sin REACT_APP_DOCS_URL todo se
 * queda en el dominio del correo, como antes.
 */

const limpiar = (url) => (url || '').replace(/\/+$/, '');

export const CORREO_URL = limpiar(process.env.REACT_APP_BACKEND_URL);
export const DOCS_URL = limpiar(process.env.REACT_APP_DOCS_URL);
export const MEET_URL = limpiar(process.env.REACT_APP_MEET_URL);

function origenActual() {
  try {
    return window.location.origin;
  } catch {
    return '';
  }
}

/** Dominios anteriores del correo. nginx redirige sus páginas a CORREO_URL, pero sigue sirviendo ahí la
 *  API, el motor y /app/office (la app ya instalada los abre en el dominio viejo). Una página abierta en
 *  un alias habla con la API de SU origen: así no depende de CORS. */
export const CORREO_ALIAS = ['https://email.orvian.mx'];

/** ¿Esta página está en un dominio anterior del correo? */
export const enAliasCorreo = () => CORREO_ALIAS.includes(origenActual());

export const docsActivo = () => !!DOCS_URL && !!CORREO_URL && DOCS_URL !== CORREO_URL;

/** ¿Esta página está en docs.orvian.mx? */
export const enDocs = () => docsActivo() && origenActual() === DOCS_URL;

/** Rutas cuyo sitio es docs. `/file/` y `/open/` dependen del archivo: lo decide DriveEditor al cargarlo.
 *  Formularios (Orvian Forms): `/form/:id` (editor), `/form/:id/preview` y `/f/:token` (responder). */
const RUTA_DOCUMENTO = /^\/(?:(?:document|spreadsheet|presentation)\/[^/]+|form\/[^/]+(?:\/preview)?)\/?$/;
const RUTA_PUBLICA_DOCS = /^\/f\/[^/]+\/?$/;
const RUTA_DOCS = /^\/(?:(?:document|spreadsheet|presentation|file|open|f)\/[^/]+|form\/[^/]+(?:\/preview)?)\/?$/;

export const esRutaDeDocumento = (pathname) => RUTA_DOCUMENTO.test(pathname || '');
/** Rutas de docs que no necesitan sesión y siempre viven allí (responder un formulario). */
export const esRutaPublicaDeDocs = (pathname) => RUTA_PUBLICA_DOCS.test(pathname || '');
export const puedeVivirEnDocs = (pathname) => RUTA_DOCS.test(pathname || '');

const rutaCompleta = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

/** Ruta (`/document/x`) → URL absoluta en docs, si estamos en el correo y docs está activo. */
export function urlEnDocs(ruta) {
  if (!docsActivo() || enDocs()) return ruta;
  return `${DOCS_URL}${ruta}`;
}

/** Navegar a una ruta del portal o a una URL de otro dominio (esa sí recarga: es otro sitio). */
export function irA(nav, destino, { reemplazar = false } = {}) {
  if (/^https?:\/\//i.test(destino)) {
    if (reemplazar) window.location.replace(destino);
    else window.location.assign(destino);
    return;
  }
  nav(destino, { replace: reemplazar });
}

/* Freno de bucles. Si docs no consigue la sesión (un navegador que parte el almacenamiento, una
   extensión que bloquea el iframe…), devuelve a la persona al correo; si el correo la volviera a
   mandar a docs, rebotaría para siempre. Así que el correo apunta a qué ruta la mandó y cuándo, y
   si esa misma ruta vuelve en pocos segundos la abre aquí mismo, como antes de existir docs.
   sessionStorage es de la pestaña: sobrevive al ir y volver entre los dos dominios. */
const SALTO = 'zuno_docs_salto';
const SALTO_MS = 20000;

function saltoReciente(ruta) {
  try {
    const previo = JSON.parse(sessionStorage.getItem(SALTO) || 'null');
    return !!previo && previo.ruta === ruta && Date.now() - previo.t < SALTO_MS;
  } catch {
    return true; // sin sessionStorage no hay freno: más vale abrir aquí que arriesgar un bucle
  }
}

/** ¿Mandar la ruta actual a docs? (sin efectos: para decidir qué pintar) */
export const puedeSaltarADocs = () => docsActivo() && !enDocs() && !saltoReciente(rutaCompleta());

export function olvidarSalto() {
  try {
    sessionStorage.removeItem(SALTO);
  } catch {
    /* sin sessionStorage no hay freno que borrar */
  }
}

/** Desde el correo: manda la ruta actual a docs. Devuelve false si se queda aquí (docs desactivado o bucle). */
export function saltarADocs() {
  if (!puedeSaltarADocs()) return false;
  const ruta = rutaCompleta();
  try {
    sessionStorage.setItem(SALTO, JSON.stringify({ ruta, t: Date.now() }));
  } catch {
    return false;
  }
  window.location.replace(`${DOCS_URL}${ruta}`);
  return true;
}

/**
 * Entrar con la cuenta y volver a esta página de docs (un formulario que pide sesión). El acceso
 * vive en el correo: Login lee `volver` (solo rutas de docs, nunca una URL de fuera) y al terminar
 * lleva ahí, y el correo a su vez manda a docs.
 */
export function entrarYVolver(ruta = `${window.location.pathname}${window.location.search}`) {
  const destino = `${CORREO_URL || ''}/login?volver=${encodeURIComponent(ruta)}`;
  window.location.assign(destino);
}

/** ¿`ruta` es un destino válido para volver tras entrar? (relativa y de docs: evita redirecciones abiertas) */
export function rutaDeVueltaValida(ruta) {
  if (typeof ruta !== 'string' || !ruta.startsWith('/') || ruta.startsWith('//')) return false;
  const soloRuta = ruta.split(/[?#]/)[0];
  return puedeVivirEnDocs(soloRuta);
}

/** Desde docs: lo que no es un documento vuelve al correo, con la misma ruta. */
export function volverAlCorreo(ruta = rutaCompleta()) {
  window.location.replace(`${CORREO_URL}${ruta}`);
}

/* ---------------- Orvian Meet (meet.orvian.mx) ----------------
   Mismo build, mismo truco que docs: la sesión la trae del correo (sesionCorreo.js). Allí solo viven
   el inicio (`/`) y las reuniones (`/abc-defg-hij`); lo demás vuelve al correo. */

export const meetActivo = () => !!MEET_URL && !!CORREO_URL && MEET_URL !== CORREO_URL;

/** ¿Esta página está en meet.orvian.mx? */
export const enMeet = () => meetActivo() && origenActual() === MEET_URL;

const RUTA_REUNION = /^\/([a-z]{3}-[a-z]{4}-[a-z]{3})\/?$/;
const RUTA_MEET = /^\/(?:[a-z]{3}-[a-z]{4}-[a-z]{3}\/?)?$/;

/** `/abc-defg-hij` → 'abc-defg-hij' (o null si la ruta no es una reunión). */
export const codigoDeReunion = (pathname) => {
  const m = RUTA_REUNION.exec((pathname || '').toLowerCase());
  return m ? m[1] : null;
};
export const puedeVivirEnMeet = (pathname) => RUTA_MEET.test((pathname || '').toLowerCase());

/** Ruta de Meet (`/` o `/abc-defg-hij`) → URL absoluta en meet.orvian.mx. */
export function urlEnMeet(ruta = '/') {
  if (!meetActivo() || enMeet()) return ruta;
  return `${MEET_URL}${ruta}`;
}

/** ¿`ruta` es un destino de Meet válido para volver tras entrar en el correo? (evita redirecciones abiertas) */
export function rutaDeVueltaMeetValida(ruta) {
  return meetActivo() && typeof ruta === 'string' && puedeVivirEnMeet(ruta);
}

/** Desde Meet: entrar con la cuenta en el correo y volver a esta reunión (Login lee `?meet=`). */
export function entrarYVolverAMeet(ruta = window.location.pathname) {
  window.location.assign(`${CORREO_URL || ''}/login?meet=${encodeURIComponent(puedeVivirEnMeet(ruta) ? ruta : '/')}`);
}
